import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  exportCertMetrics,
  fetchCertByStandardMetrics,
  fetchCertDrilldown,
  fetchCertMetrics,
} from '@/api/report';
import type { ICertDrilldownBody } from '@/api/report/types';
import { useTranslator } from '@/hooks/useTranslator';
import { useSessionStore } from '@/store/sessionStore';
import { stripCertMetricsFilter } from '../-utils/certFilterUtils';

// ── Main metrics ─────────────────────────────────────────────────────────────

export function certMetricsQueryOptions(companyId: string, filter: string) {
  const certFilter = stripCertMetricsFilter(filter);
  return queryOptions({
    queryKey: ['report', 'cert-metrics', companyId, certFilter] as const,
    queryFn: () =>
      fetchCertMetrics(companyId, certFilter).then(({ data }) => data),
    enabled: !!companyId,
  });
}

export function useCertMetricsQuery(filter: string) {
  const companyId = useSessionStore(s => s.user?.companyId ?? '');
  return useQuery(certMetricsQueryOptions(companyId, filter));
}

// ── Historic data (by standard) ──────────────────────────────────────────────

export function useCertByStandardQuery(filter: string) {
  const companyId = useSessionStore(s => s.user?.companyId ?? '');
  return useQuery({
    queryKey: ['report', 'cert-by-standard', companyId, filter] as const,
    queryFn: () =>
      fetchCertByStandardMetrics(companyId, filter).then(({ data }) => data),
    enabled: !!companyId && !!filter,
  });
}

// ── Drilldown table ───────────────────────────────────────────────────────────

export function useCertDrilldownQuery(body: ICertDrilldownBody) {
  return useQuery({
    queryKey: [
      'report',
      'cert-drilldown',
      body.companyId,
      body.page,
      body.limit, // driven by table.limit (user-selected rows per page)
      body.sort,
      body.order,
      body.supplierCompanyIds?.join(',') ?? '',
      body.collaborationStatusIds?.join(',') ?? '',
      body.standardIds?.join(',') ?? '',
      body.expirationDate ?? '',
      body.status ?? '',
      body.expirationStatus ?? '',
      body.depthLevel ?? '',
    ] as const,
    queryFn: () => fetchCertDrilldown(body),
    enabled: !!body.companyId,
    placeholderData: prev => prev,
  });
}

// ── Export ────────────────────────────────────────────────────────────────────

export function useCertExport() {
  const { getTranslation: t } = useTranslator({ page: 'certCompliance' });
  const mutation = useMutation({
    mutationFn: (filter: string) => exportCertMetrics(filter),
  });
  const handleExport = async (filter: string) => {
    try {
      await mutation.mutateAsync(filter);
      toast.success(t('export.success'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('export.error'));
    }
  };
  return { handleExport, isExporting: mutation.isPending };
}
